import React from 'react';
import { FaDatabase } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useActiveProject } from '../../context/ActiveProjectContext';
import ActiveIdentityLabel from './ActiveIdentityLabel';
import { useSidebar } from '../../context/SidebarContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { COMPACT_FOOTER_MEDIA_QUERY } from '../../constants/viewport';

const DB_STATUS = {
  connected: { label: 'Connected', color: 'var(--archon-status-connected)', title: 'Database connected' },
  disconnected: { label: 'Disconnected', color: 'var(--archon-status-disconnected)', title: 'Database disconnected' },
};

function DbConnectionStatus({ dbStatus, iconSize = 15, fontSize = '0.95em' }) {
  const status = DB_STATUS[dbStatus] ?? DB_STATUS.disconnected;
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: '6px', lineHeight: 1 }}
      title={status.title}
      aria-label={status.title}
    >
      <FaDatabase size={iconSize} style={{ color: status.color, flexShrink: 0 }} aria-hidden />
      <span style={{ fontSize, color: status.color, fontWeight: 500 }}>{status.label}</span>
    </div>
  );
}

const Footer = ({ dbStatus }) => {
  const auth = useAuth();
  const { activeProject } = useActiveProject();
  const { effectiveSidebarWidthPx } = useSidebar();
  const isMobile = useMediaQuery(COMPACT_FOOTER_MEDIA_QUERY);

  const dbAria = (DB_STATUS[dbStatus] ?? DB_STATUS.disconnected).title;

  const devCredit = (
    <span style={{ fontSize: '0.72em', color: 'var(--archon-footer-text-soft)', textAlign: 'center', lineHeight: 1.35 }}>
      ArchonPM v1.0 Beta — Developed by{' '}
      <a
        href="https://www.ChrisallTech.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--archon-footer-link)', textDecoration: 'none' }}
      >
        Chrisall Technologies
      </a>
    </span>
  );

  // Mobile: solo estado DB (Company/Project en cabecera o pantallas dedicadas)
  if (isMobile) {
    return (
      <footer
        className="footer"
        data-tour="app-footer"
        role="contentinfo"
        aria-label={dbAria}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          backgroundColor: 'var(--archon-navy)',
          color: 'white',
          padding: '6px 12px',
          borderTop: '1px solid var(--archon-footer-border)',
          zIndex: 999,
          flexShrink: 0
        }}
      >
        <DbConnectionStatus dbStatus={dbStatus} iconSize={14} fontSize="0.85em" />
        {devCredit}
      </footer>
    );
  }

  // Desktop styles - horizontal + developer credit row
  return (
    <footer
      className="footer"
      data-tour="app-footer"
      style={{
        position: 'fixed',
        bottom: 0,
        left: `${effectiveSidebarWidthPx}px`,
        width: `calc(100% - ${effectiveSidebarWidthPx}px)`,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--archon-navy)',
        color: 'white',
        padding: '6px 16px 8px',
        borderTop: '1px solid var(--archon-footer-border)',
        zIndex: 999,
        flexShrink: 0,
        transition: 'left 0.3s ease, width 0.3s ease'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <div style={{ width: '33%', display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '0.9em' }}>
            <span style={{ color: 'var(--archon-footer-text-muted)', marginRight: '5px' }}>Company:</span>
            <span style={{ color: 'var(--archon-accent)', fontWeight: 'bold' }}>
              {auth.user?.company?.name || '—'}
            </span>
          </span>
        </div>
        <div style={{ width: '34%', textAlign: 'center' }}>
          <span style={{ fontSize: '0.9em' }}>
            <span style={{ color: 'var(--archon-footer-text-muted)', marginRight: '5px' }}>Project:</span>
            <span style={{ color: 'var(--archon-accent)', fontWeight: 'bold' }}>
              {activeProject?.project_name || '—'}
            </span>
          </span>
        </div>
        <div style={{ width: '33%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
          {/* Con qué identidad se está hablando con el servidor. Ver
              ActiveIdentityLabel: el nombre sale del token, no del perfil. */}
          <ActiveIdentityLabel />
          <DbConnectionStatus dbStatus={dbStatus} />
        </div>
      </div>
      <div style={{ width: '100%', textAlign: 'center', paddingTop: '4px', borderTop: '1px solid var(--archon-footer-border-soft)' }}>
        {devCredit}
      </div>
    </footer>
  );
};

export default Footer;